
import React, { useState, useEffect } from 'react';
import { AppSettings, OpsChecklistView, OpsTreeView } from '../types';
import { Activity, Server, AlertTriangle, CheckCircle, RefreshCw, LayoutGrid, Box, ChevronRight, ChevronDown, Database, Shield } from 'lucide-react';

interface OpsCockpitProps {
  settings: AppSettings;
}

const OpsCockpit: React.FC<OpsCockpitProps> = ({ settings }) => {
  const [checklist, setChecklist] = useState<OpsChecklistView | null>(null);
  const [tree, setTree] = useState<OpsTreeView | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedStreams, setExpandedStreams] = useState<Set<string>>(new Set(['chassis', 'products', 'mobile', 'operations']));

  useEffect(() => {
    if (settings.useBackend) {
      fetchOpsData();
    }
  }, [settings.useBackend, settings.dbConfig]);

  const fetchOpsData = async () => {
    setLoading(true);
    try {
      // Parallel Fetch
      const [checkRes, treeRes] = await Promise.all([
        fetch(`${settings.backendUrl}/api/ops/startup-checklist`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dbConfig: settings.dbConfig })
        }),
        fetch(`${settings.backendUrl}/api/ops/project-tree`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dbConfig: settings.dbConfig })
        })
      ]);

      const checkData = await checkRes.json();
      const treeData = await treeRes.json();

      if (checkData.success) setChecklist(checkData);
      if (treeData.success) setTree(treeData);

    } catch (err) {
      console.error("Failed to load Ops Data", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStream = (streamId: string) => {
    const next = new Set(expandedStreams);
    if (next.has(streamId)) next.delete(streamId);
    else next.add(streamId);
    setExpandedStreams(next);
  };

  if (!settings.useBackend) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 bg-slate-950 p-8">
        <Server className="w-16 h-16 mb-4 opacity-50" />
        <h2 className="text-xl font-semibold text-slate-300">Ops Cockpit Offline</h2>
        <p className="max-w-md text-center mt-2">
          The Operations Cockpit requires a live connection to the KVM4 Backend and KVM8 Database. 
          Please enable "Remote Backend Mode" in Settings.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-200">
      
      {/* Ops Header */}
      <div className="h-14 border-b border-slate-800 flex items-center justify-between px-6 bg-slate-900/50">
        <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-red-500" />
            <span className="font-semibold tracking-tight">System Operations</span>
            {loading && <RefreshCw className="w-4 h-4 animate-spin text-slate-500 ml-2" />}
        </div>
        <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 rounded border border-slate-800">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span>Healthy: {checklist?.summary?.up || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 rounded border border-slate-800">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span>Warning: {checklist?.summary?.warning || 0}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 rounded border border-slate-800">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span>Down: {checklist?.summary?.down || 0}</span>
            </div>
            <button 
                onClick={fetchOpsData} 
                className="p-1.5 hover:bg-slate-800 rounded transition-colors"
                title="Refresh Ops Data"
            >
                <RefreshCw className="w-4 h-4" />
            </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
          
          {/* LEFT: Project Tree */}
          <div className="w-80 border-r border-slate-800 flex flex-col bg-slate-900/20">
             <div className="p-4 border-b border-slate-800 text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <LayoutGrid className="w-4 h-4" /> Stream Architecture
             </div>
             <div className="flex-1 overflow-y-auto p-2">
                 {tree?.nodes.map((stream) => (
                     <div key={stream.id} className="mb-2">
                         <button 
                            onClick={() => toggleStream(stream.id)}
                            className="w-full flex items-center gap-2 p-2 hover:bg-slate-800 rounded-lg transition-colors text-left"
                         >
                            {expandedStreams.has(stream.id) ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
                            <div className="w-2 h-8 rounded-full" style={{ backgroundColor: stream.color || '#64748b' }}></div>
                            <span className="font-medium text-sm text-slate-200">{stream.label}</span>
                         </button>
                         
                         {expandedStreams.has(stream.id) && (
                             <div className="ml-6 mt-1 space-y-1 border-l border-slate-800 pl-2">
                                 {stream.children && stream.children.length > 0 ? (
                                    stream.children.map((proj) => (
                                        <div key={proj.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-800/50 rounded cursor-pointer group">
                                            <Box className="w-3 h-3 text-slate-600 group-hover:text-indigo-400" />
                                            <span className="text-xs text-slate-400 group-hover:text-slate-200">{proj.label}</span>
                                            <span className={`ml-auto w-1.5 h-1.5 rounded-full ${
                                                proj.status === 'up' ? 'bg-emerald-500' : 'bg-slate-600'
                                            }`}></span>
                                        </div>
                                    ))
                                 ) : (
                                     <div className="text-[10px] text-slate-600 italic px-2 py-1">No projects linked</div>
                                 )}
                             </div>
                         )}
                     </div>
                 ))}
             </div>
          </div>

          {/* CENTER: Startup Checklist */}
          <div className="flex-1 flex flex-col bg-slate-950 p-8 overflow-y-auto">
             <div className="max-w-3xl mx-auto w-full">
                <h2 className="text-2xl font-bold text-white mb-2">Session Startup Checklist</h2>
                <p className="text-slate-400 mb-8">System health verification for KVM4/KVM8 Mesh.</p>
                
                <div className="space-y-4">
                    {checklist?.items.map((item) => (
                        <div key={item.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-4 transition-all hover:border-slate-700">
                             <div className={`mt-1 p-2 rounded-full shrink-0 ${
                                 item.status === 'up' ? 'bg-emerald-900/20 text-emerald-400' :
                                 item.status === 'warning' ? 'bg-amber-900/20 text-amber-400' :
                                 'bg-red-900/20 text-red-400'
                             }`}>
                                 {item.status === 'up' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                             </div>
                             <div className="flex-1">
                                 <div className="flex justify-between items-start">
                                     <h3 className="font-semibold text-slate-200">{item.label}</h3>
                                     <span className="text-[10px] uppercase tracking-wider text-slate-600 bg-slate-950 px-2 py-0.5 rounded border border-slate-800">{item.category}</span>
                                 </div>
                                 <p className="text-sm text-slate-400 mt-1">{item.detail}</p>
                             </div>
                             {item.actions && item.actions.length > 0 && (
                                 <div className="flex gap-2">
                                     {item.actions.map(action => (
                                         <button key={action} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-300 rounded-lg transition-colors border border-slate-700">
                                             {action.charAt(0).toUpperCase() + action.slice(1)}
                                         </button>
                                     ))}
                                 </div>
                             )}
                        </div>
                    ))}
                </div>
             </div>
          </div>

          {/* RIGHT: Quick Stats / Governance (Placeholder for V_PENDING_APPROVALS) */}
          <div className="w-72 border-l border-slate-800 bg-slate-900/20 p-4">
             <div className="mb-6">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Database className="w-4 h-4" /> Infrastructure
                </h3>
                <div className="space-y-2">
                    <div className="bg-slate-900 p-3 rounded border border-slate-800">
                        <div className="text-xs text-slate-500 mb-1">Database Node</div>
                        <div className="text-sm font-mono text-slate-300">KVM8_US</div>
                        <div className="flex items-center gap-1 mt-2">
                             <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                             <span className="text-[10px] text-emerald-400">Online</span>
                        </div>
                    </div>
                    <div className="bg-slate-900 p-3 rounded border border-slate-800">
                        <div className="text-xs text-slate-500 mb-1">App Node</div>
                        <div className="text-sm font-mono text-slate-300">KVM4_US</div>
                        <div className="flex items-center gap-1 mt-2">
                             <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                             <span className="text-[10px] text-emerald-400">Online</span>
                        </div>
                    </div>
                </div>
             </div>

             <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Governance
                </h3>
                {/* This would ideally fetch from v_pending_approvals */}
                <div className="p-4 border border-dashed border-slate-800 rounded text-center">
                    <p className="text-xs text-slate-500">No pending approvals.</p>
                </div>
             </div>
          </div>

      </div>
    </div>
  );
};

export default OpsCockpit;
